
import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import { mapGetters } from "vuex";
import {
  IPagination,
  ITh,
  IStatusFilter,
  IFilterSettings,
  ISelectOption,
} from "@/models/Global";
import { IUser } from "@/models/User";
import {
  FILTER_STATUS_OPTIONS,
  LIST_TH,
  LIST_TH_MF,
  LIST_TH_MS,
  MORTGAGES_FILTERS_SORT_KEYS,
} from "@/constants/mortgages";
import MortgagesTableBody from "@/components/mortgages/MortgagesTableBody.vue";
import MortgagesStatus from "@/components/mortgages/MortgagesStatus.vue";
import MortgagesDocuments from "@/components/mortgages/MortgagesDocuments.vue";
import MortgagesComments from "@/components/mortgages/MortgagesComments.vue";
import MortgagesContact from "@/components/mortgages/MortgagesContact.vue";
import MortgagesInfo from "@/components/mortgages/MortgagesInfo.vue";
import HiboLateralPanel from "@/components/ui/HiboLateralPanel.vue";
import CustomTableWrapper from "@/components/common/CustomTableWrapper.vue";
import HiboMultiSelect from "../ui/HiboMultiSelect.vue";
import { IMortgageData, IPanelConfig } from "@/models/Mortgages";
import { IFranchise } from "@/models/Auth";
import {
  AVALON_PROPERTIES_ID,
  MASTER_OF,
  MASTERS_FRANCHISES,
} from "@/constants";

@Component({
  computed: {
    ...mapGetters("auth", {
      user: "getUser",
      franchises: "getFranchises",
    }),
  },
  components: {
    CustomTableWrapper,
    MortgagesTableBody,
    HiboLateralPanel,
    MortgagesStatus,
    MortgagesDocuments,
    MortgagesComments,
    MortgagesContact,
    MortgagesInfo,
  },
})
export default class MortgagesWrapper extends Vue {
  @Inject() readonly curr_business!: string;

  protected user!: IUser;
  protected franchises!: IFranchise[];
  $refs!: {
    multiselect: HiboMultiSelect[] | undefined;
  };

  @Prop({ type: Array, required: true })
  protected operations!: IMortgageData[];

  @Prop({ type: Boolean, required: true })
  protected loading!: boolean;

  @Prop({ type: Object, required: true })
  protected pagination!: IPagination;

  @Prop({ type: Array, required: true })
  protected statusList!: IStatusFilter[];

  @Prop({ type: String, default: "" })
  protected lostReasonPrefix?: string;

  protected panelConfigs: IPanelConfig = {
    tabs: null,
    lateralPanelStatus: null,
    selectedMortgage: null,
    selectedMortgagesStatus: 10,
    selectedMortgageId: null,
    selectedUserName: null,
    stepsList: [],
  };

  protected get tableThConfig(): ITh[] {
    return this.user.isMaster && this.isMasterOfSalesperson
      ? [...LIST_TH, ...LIST_TH_MS]
      : this.user.isMaster
      ? [...LIST_TH, ...LIST_TH_MF]
      : LIST_TH;
  }

  protected get queryParams(): any {
    return this.$route?.query;
  }

  protected get searchId(): number | null {
    return +this.queryParams?.id || null;
  }

  protected getLabel(franchise: IFranchise) {
    return franchise.agentId
      ? `${franchise.agentId} - ${franchise.firstName}`
      : `${franchise.firstName} ${franchise.lastName}`;
  }

  protected get filterFranchisesOptions(): ISelectOption[] {
    const options: ISelectOption[] = [
      { label: "all", name: "all", value: null },
    ];

    if (this.user.agentId)
      options.push({
        label: "myLeads",
        name: "myLeads",
        value: this.user.agentId,
      });

    this.franchises?.forEach((franchise: IFranchise) =>
      franchise.agentId
        ? options.push({
            label: this.getLabel(franchise),
            name: "franchise_" + franchise.agentId,
            value: franchise.agentId,
          })
        : options.push({
            label: this.getLabel(franchise),
            name: "franchise_" + franchise.housfyAccountId,
            value: franchise.housfyAccountId,
          })
    );

    return options;
  }

  protected filtersValues: (number[] | string[] | number | string | null)[] = [
    null,
    this.searchId || null,
    this.queryParams?.status || null,
  ];

  protected get filters(): IFilterSettings[] {
    return [
      {
        name: MORTGAGES_FILTERS_SORT_KEYS.FRANCHISE.filter,
        labelKey: this.isMasterOfSalesperson
          ? MASTER_OF.SALESPERSON
          : MASTER_OF.FRANCHISES,
        options: this.filterFranchisesOptions,
        value: this.filtersValues[0],
        availableLevel: this.user.isMaster && !this.isAvalonProperties ? 1 : 0,
        hierarchy: 0,
        filterable: true,
        multiSelect: false,
      },
      {
        name: MORTGAGES_FILTERS_SORT_KEYS.LEAD_ID.filter,
        labelKey: "id",
        options: [],
        value: this.filtersValues[1],
        availableLevel: 0,
        hierarchy: 0,
        filterable: false,
        multiSelect: false,
      },
      {
        name: MORTGAGES_FILTERS_SORT_KEYS.STATUS.filter,
        labelKey: "status",
        options: FILTER_STATUS_OPTIONS,
        value: this.filtersValues[2],
        availableLevel: 1,
        hierarchy: 0,
        filterable: false,
        multiSelect: false,
      },
    ];
  }

  protected paginateOperations(dir: string) {
    let page = this.pagination.page || 1;
    if (dir === "next" && page < (this.pagination.totalPages || 0)) page++;
    else if (dir === "prev" && page > 1) page--;
    this.filterOperations({ page: page });
  }

  protected sortOperations(thId: string) {
    const sortedItem = this.tableThConfig.find((t) => t.id === thId);
    if (sortedItem)
      sortedItem.sortDirection =
        sortedItem?.sortDirection === "desc" ? "asc" : "desc";
    this.filterOperations({
      page: 1,
      sortField: thId,
      sortDirection: sortedItem?.sortDirection || "desc",
    });
  }

  protected removeFilterById() {
    if (!this.$route.query.id) return;
    const query = Object.assign({}, this.$route.query);
    delete query.id;
    this.$router.push({ query });
    this.setFilter(MORTGAGES_FILTERS_SORT_KEYS.LEAD_ID.filter, 0);
  }

  protected setFilter(filterName: string, value: number | string | null) {
    const index = this.filters.findIndex(
      (f: IFilterSettings) => f.name === filterName
    );
    Vue.set(this.filtersValues, index, value);
    this.filterOperations();
    this.retrieveFiltersOptions(index);
  }

  protected async retrieveFiltersOptions(index: number | null = null) {
    if (index !== null)
      this.filters.forEach((f: IFilterSettings, i: number) => {
        if (
          f.hierarchy &&
          (f.hierarchy > this.filters[index].hierarchy ||
            (f.hierarchy === this.filters[index].hierarchy && index !== i))
        )
          Vue.set(this.filtersValues, i, null);
      });
  }

  protected async filterOperations(
    pagination: IPagination | null = { page: 1 }
  ) {
    await this.$store.dispatch(this.curr_business + "/retrieveOperations", {
      pagination: pagination,
      filters: this.filters,
    });
  }

  protected resetFilters() {
    for (let i = 0; i <= this.filtersValues.length - 1; i++)
      Vue.set(this.filtersValues, i, null);
  }

  protected isStatusFilter(filter: string): boolean {
    return filter === MORTGAGES_FILTERS_SORT_KEYS.STATUS.filter;
  }

  protected isFranchisesFilter(filter: string): boolean {
    return filter === MORTGAGES_FILTERS_SORT_KEYS.FRANCHISE.filter;
  }

  protected get isMasterOfSalesperson(): boolean {
    const isMasterOf: string =
      this.user.id && MASTERS_FRANCHISES.includes(this.user.id)
        ? MASTER_OF.FRANCHISES
        : MASTER_OF.SALESPERSON;
    return isMasterOf === MASTER_OF.SALESPERSON;
  }

  protected async handleMortgageUpdated(mortgageId: number) {
    const updatedMortgage = await this.$store.dispatch(
      "mortgages/getMortgage",
      mortgageId
    );
    if (updatedMortgage && this.panelConfigs.selectedMortgage) {
      this.panelConfigs.selectedMortgage = updatedMortgage;
    }
  }

  protected get isAvalonProperties(): boolean {
    const targetId = AVALON_PROPERTIES_ID;
    return this.user?.agentId === targetId || this.user?.parentId === targetId;
  }

  mounted() {
    this.filterOperations();
    this.retrieveFiltersOptions();
    console.log(this.user);
  }

  @Watch("searchId")
  searchUpdated() {
    this.resetFilters();
    Vue.set(this.filtersValues, 1, this.searchId);
    this.retrieveFiltersOptions(0);
    if (this.searchId) this.filterOperations();
    if (this.$refs.multiselect) {
      this.$refs.multiselect[0].forceReset();
    }
  }
}
